import { memo, useCallback, useMemo, useState } from "react";

import type { MFASettingsType, UpdateMfaSettingsResultType } from "@/hooks/use-auth-client/use-mfa-settings";

import { FeatureIslandHeader } from "@/components/features/_layouts";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";

import { IslandMfaSettings } from "./components";

type Props = {
	mfaSettings: MFASettingsType;
	updateMfaSettings: (mfaSettings: MFASettingsType) => Promise<UpdateMfaSettingsResultType>;
};

export const SettingsSecurityPresenter = memo(({ mfaSettings, updateMfaSettings }: Props) => {
	const [state, setState] = useState({
		current: mfaSettings,
		lastSaved: mfaSettings,
	});

	/**
	 * 現在の設定を更新
	 */
	const updateCurrent = useCallback((newSettings: MFASettingsType) => {
		setState((prev) => ({ ...prev, current: newSettings }));
	}, []);

	/**
	 * 最後に保存した設定を更新
	 */
	const syncLastSaved = useCallback(() => {
		setState((prev) => ({ ...prev, lastSaved: prev.current }));
	}, []);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	/**
	 * 差分があるかどうかを判定（最後に保存した状態と比較）
	 */
	const hasChanges = useMemo(() => state.current.mfaStatus !== state.lastSaved.mfaStatus, [state]);

	/**
	 * 保存ボタン押下時の処理
	 */
	const handleSave = useCallback(async () => {
		if (!hasChanges) return;

		showLoadingScreenCallback();

		const res = await updateMfaSettings(state.current) //
			.finally(() => {
				hideLoadingScreenCallback();
			});

		if (!res.success) {
			notifyUserEventInfo({
				variant: "error",
				title: "２段階認証設定を更新できませんでした",
				description: "時間を置いて再度お試しください",
			});
			return;
		}

		// 保存成功時は最後に保存した状態を更新（差分検知をリセット）
		syncLastSaved();

		notifyUserEventInfo({
			title: "２段階認証設定を更新しました",
		});
	}, [hasChanges, state, updateMfaSettings, showLoadingScreenCallback, hideLoadingScreenCallback, syncLastSaved]);

	/**
	 * 設定が変更された時の処理
	 */
	const handleLocalSettingsChange = useCallback(
		(newSettings: MFASettingsType) => {
			updateCurrent(newSettings);
		},
		[updateCurrent],
	);

	return (
		<>
			<FeatureIslandHeader
				title="セキュリティ設定"
				rightChildren={
					<Button size="xl" disabled={!hasChanges} onClick={handleSave}>
						保存
					</Button>
				}
			/>
			<IslandMfaSettings mfaSettings={state.current} onSettingsChange={handleLocalSettingsChange} />
		</>
	);
});

SettingsSecurityPresenter.displayName = "SettingsSecurityPresenter";
